import React, { useEffect, useMemo, useState } from "react";
import CalendarIcon from "../../../img/calendar_month.svg";
import CustomSelect from "../../Shared/CustomSelect/CustomSelect";
import { userService } from "../../UserList/services/userService";
import EmptyCard from "../../InventoryManagement/components/Common/EmptyCard";
import ErrorCard from "../../InventoryManagement/components/Common/ErrorCard";

const months = [
  { month_number: 1, short_name: "Jan", month_name: "January" },
  { month_number: 2, short_name: "Feb", month_name: "February" },
  { month_number: 3, short_name: "Mar", month_name: "March" },
  { month_number: 4, short_name: "Apr", month_name: "April" },
  { month_number: 5, short_name: "May", month_name: "May" },
  { month_number: 6, short_name: "Jun", month_name: "June" },
  { month_number: 7, short_name: "Jul", month_name: "July" },
  { month_number: 8, short_name: "Aug", month_name: "August" },
  { month_number: 9, short_name: "Sep", month_name: "September" },
  { month_number: 10, short_name: "Oct", month_name: "October" },
  { month_number: 11, short_name: "Nov", month_name: "November" },
  { month_number: 12, short_name: "Dec", month_name: "December" },
];

const RoleHistory = ({ userId }) => {
  const currentDate = new Date();
  const currentMonthNumber = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const availableYears = data?.available_years || [Number(selectedYear)];

  useEffect(() => {
    setSelectedYear(String(currentYear));
    setData(null);
    setError(null);
  }, [currentYear, userId]);

  useEffect(() => {
    const fetchRoleHistory = async () => {
      try {
        setIsLoading(true);
        const response = await userService.getRoleHistory(userId, selectedYear);
        setData(response);
      } catch (error) {
        console.error("Error fetching role history:", error);
        setData(null);
        setError("Failed to load role history data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleHistory();
  }, [selectedYear, userId]);

  const normalizedMonths = useMemo(() => {
    const apiMonths = data?.months || [];
    const monthMap = new Map(
      apiMonths.map((month) => [month.month_number, month])
    );

    return months.map((month) => {
      const apiMonth = monthMap.get(month.month_number);

      return {
        month_number: month.month_number,
        month_name: apiMonth?.month_name || month.month_name,
        short_name: month.short_name,
        has_data: Boolean(apiMonth?.has_data),
        user_role: apiMonth?.user_role || null,
        tagged_geography: apiMonth?.tagged_geography || null,
        tagging: apiMonth?.tagging || [],
        color: apiMonth?.color || "#9CA3AF",
      };
    });
  }, [data]);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const defaultMonth = normalizedMonths.find((month) => month.has_data);
    return (
      defaultMonth?.month_name || normalizedMonths[0]?.month_name || "January"
    );
  });

  useEffect(() => {
    const matchedMonth = normalizedMonths.find(
      (month) => month.month_name === selectedMonth
    );

    if (!matchedMonth) {
      const defaultMonth = normalizedMonths.find((month) => month.has_data);
      setSelectedMonth(
        defaultMonth?.month_name || normalizedMonths[0]?.month_name || "January"
      );
    }
  }, [normalizedMonths, selectedMonth]);

  const activeMonths = useMemo(
    () => normalizedMonths.filter((month) => month.has_data).length,
    [normalizedMonths]
  );

  const hasRoleHistory = useMemo(
    () => normalizedMonths.some((month) => month.has_data),
    [normalizedMonths]
  );

  const groupedMonths = useMemo(() => {
    const dataMonths = normalizedMonths.filter((month) => month.has_data);
    if (dataMonths.length === 0) return [];

    const groups = [];
    let currentGroup = {
      months: [dataMonths[0]],
      color: dataMonths[0].color,
      user_role: dataMonths[0].user_role,
      tagged_geography: dataMonths[0].tagged_geography,
      tagging: dataMonths[0].tagging,
    };

    for (let i = 1; i < dataMonths.length; i++) {
      const month = dataMonths[i];
      if (month.color === currentGroup.color) {
        currentGroup.months.push(month);
      } else {
        groups.push(currentGroup);
        currentGroup = {
          months: [month],
          color: month.color,
          user_role: month.user_role,
          tagged_geography: month.tagged_geography,
          tagging: month.tagging,
        };
      }
    }
    groups.push(currentGroup);

    return groups;
  }, [normalizedMonths]);

  if (isLoading) {
    return <RoleHistorySkeleton />;
  }

  if (error) {
    return (
      <ErrorCard
        title="Error Loading Role History"
        message={error}
        onRetryClick={() => setSelectedYear(String(currentYear))}
      />
    );
  }

  return (
    <div className="role-history-panel">
      <div className="role-history-toolbar">
        <CustomSelect
          item={{
            label: "Select Year",
            icon: CalendarIcon,
            key: "year",
            value: selectedYear,
            options: availableYears.map((year) => ({
              id: String(year),
              name: String(year),
            })),
          }}
          style={{
            border: "1px solid #362B7314",
            boxShadow: "none",
            width: "280px",
          }}
          handleChange={(event) => setSelectedYear(event.target.value)}
          showExpandIndicator={true}
          isSearchable={false}
        />
      </div>
      {!hasRoleHistory ? (
        <EmptyCard
          title="No Role History Available"
          message="Role history data is not available for the selected year."
        />
      ) : (
        <>
          <div className="role-history-timeline">
            <div className="role-history-months">
              {normalizedMonths.map((month) => (
                <button
                  key={month.month_number}
                  type="button"
                  className={`role-history-month-item ${
                    selectedMonth === month.month_name ? "active" : ""
                  }`}
                  onClick={() => setSelectedMonth(month.month_name)}
                >
                  <span>{month.short_name}</span>
                  <div className="role-history-month-line" />
                </button>
              ))}
            </div>

            <div className="role-history-progress-track" aria-hidden="true">
              {normalizedMonths.map((month, index) => (
                <div
                  key={month.month_number}
                  className={`role-history-progress-segment ${index < activeMonths ? "active" : "inactive"} ${
                    selectedMonth === month.month_name
                      ? "role-history-progress-segment-selected"
                      : ""
                  }`}
                  style={{ background: month.color }}
                  onClick={() => setSelectedMonth(month.month_name)}
                />
              ))}
            </div>
          </div>
          <div className="role-history-table-wrapper">
            <table className="role-history-table">
              <thead>
                <tr>
                  <th className="tagging-color-indicator" />
                  <th>Month</th>
                  <th>User Role</th>
                  <th>Tagged Geography</th>
                  <th>Tagging</th>
                </tr>
              </thead>
              <tbody>
                {groupedMonths.map((group) => {
                  const firstMonth = group.months[0];
                  const lastMonth = group.months[group.months.length - 1];
                  const isMultiMonth = group.months.length > 1;
                  const isHighlighted = group.months.some(
                    (m) => m.month_name === selectedMonth
                  );
                  const hasPresentMonth =
                    Number(selectedYear) === currentYear &&
                    group.months.some(
                      (m) => m.month_number === currentMonthNumber
                    );
                  const monthLabel = isMultiMonth
                    ? `${firstMonth.short_name}-${lastMonth.short_name}`
                    : firstMonth.short_name;

                  return (
                    <tr
                      key={firstMonth.month_number}
                      className={isHighlighted ? "highlighted-history-row" : ""}
                    >
                      <td className="tagging-color-indicator">
                        <div
                          style={{
                            width: "17px",
                            height: "17px",
                            border: "1.5px solid #362B73",
                            borderRadius: "2px",
                            backgroundColor: group.color || "#9CA3AF",
                            margin: "0 auto",
                          }}
                        />
                      </td>
                      <td>
                        {monthLabel}
                        {hasPresentMonth && (
                          <span className="role-history-present-label">
                            {" "}
                            (Present)
                          </span>
                        )}
                      </td>
                      <td>{group.user_role || "-"}</td>
                      <td>{group.tagged_geography || "-"}</td>
                      <td>
                        <div className="tag-pill-list">
                          {group.tagging.length > 0
                            ? group.tagging.map((tag) => (
                                <span key={tag} className="tag-pill">
                                  {tag}
                                </span>
                              ))
                            : "-"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const RoleHistorySkeleton = () => {
  return (
    <div className="role-history-panel">
      <div className="role-history-loading">
        <div className="role-history-loading-toolbar">
          <div
            className="skeleton-shimmer"
            style={{ width: 280, height: 44, borderRadius: 999 }}
          />
        </div>

        <div className="role-history-loading-timeline">
          <div className="role-history-loading-months">
            {months.map((month) => (
              <div
                key={month.month_number}
                className="role-history-loading-month-item"
              >
                <div
                  className="skeleton-shimmer"
                  style={{ width: 24, height: 10, borderRadius: 4 }}
                />
                <div
                  className="skeleton-shimmer"
                  style={{ width: 1, height: 28, borderRadius: 1 }}
                />
              </div>
            ))}
          </div>

          <div className="role-history-loading-progress-track">
            {months.map((month) => (
              <div
                key={month.month_number}
                className="skeleton-shimmer role-history-loading-progress-segment"
                style={{ height: 12, borderRadius: 2 }}
              />
            ))}
          </div>
        </div>

        <div className="role-history-loading-table">
          <div className="role-history-loading-table-row role-history-loading-table-row--header">
            <div
              className="skeleton-shimmer"
              style={{ width: "100%", height: 12, borderRadius: 4 }}
            />
            <div
              className="skeleton-shimmer"
              style={{ width: "100%", height: 12, borderRadius: 4 }}
            />
            <div
              className="skeleton-shimmer"
              style={{ width: "100%", height: 12, borderRadius: 4 }}
            />
            <div
              className="skeleton-shimmer"
              style={{ width: "100%", height: 12, borderRadius: 4 }}
            />
            <div
              className="skeleton-shimmer"
              style={{ width: "100%", height: 12, borderRadius: 4 }}
            />
          </div>

          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="role-history-loading-table-row">
              <div
                className="skeleton-shimmer"
                style={{ width: 17, height: 17, borderRadius: 2 }}
              />
              <div
                className="skeleton-shimmer"
                style={{ width: "75%", height: 12, borderRadius: 4 }}
              />
              <div
                className="skeleton-shimmer"
                style={{ width: "90%", height: 12, borderRadius: 4 }}
              />
              <div
                className="skeleton-shimmer"
                style={{ width: "90%", height: 12, borderRadius: 4 }}
              />
              <div
                className="skeleton-shimmer"
                style={{ width: "95%", height: 12, borderRadius: 999 }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoleHistory;
